import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import ApiService from '@/services/api';
import { IEmployeeListItem } from '@/types/workshift';
import { useAuth } from './AuthContext';
import { logger } from '@/utils/logger';

interface EmployeeContextType {
  employees: IEmployeeListItem[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

const EmployeeContext = createContext<EmployeeContextType | undefined>(undefined);

export const EmployeeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, isInternal } = useAuth();
  const [employees, setEmployees] = useState<IEmployeeListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchEmployees = useCallback(async () => {
    // Only fetch if authenticated and user is internal
    if (!isAuthenticated || !isInternal()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await ApiService.getEmployees();
      setEmployees(data);
      setHasFetched(true);
    } catch (err) {
      logger.error('Failed to fetch employees:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch employees'));
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, isInternal]);

  // Fetch employees once after login (when authenticated and internal)
  useEffect(() => {
    if (isAuthenticated && isInternal() && !hasFetched && !isLoading) {
      fetchEmployees();
    }
  }, [isAuthenticated, isInternal, hasFetched, isLoading, fetchEmployees]);

  // Reset state when logged out
  useEffect(() => {
    if (!isAuthenticated) {
      setEmployees([]);
      setHasFetched(false);
      setError(null);
    }
  }, [isAuthenticated]);

  const refetch = useCallback(() => {
    setHasFetched(false);
    fetchEmployees();
  }, [fetchEmployees]);

  return (
    <EmployeeContext.Provider
      value={{
        employees,
        isLoading,
        error,
        refetch,
      }}
    >
      {children}
    </EmployeeContext.Provider>
  );
};

export const useEmployees = (): EmployeeContextType => {
  const context = useContext(EmployeeContext);
  if (context === undefined) {
    throw new Error('useEmployees must be used within an EmployeeProvider');
  }
  return context;
};
